import React, { useState, useEffect } from 'react';
import {
  ArrowLeft,
  Reply,
  ReplyAll,
  Forward,
  Star,
  Lock,
  Unlock,
  Trash2,
  Archive,
  MoreHorizontal,
  Printer,
  Tag,
  ChevronDown,
  ChevronUp,
  Download,
  FileText,
  FileSpreadsheet,
  Image,
  ExternalLink,
  Shield,
  Loader2,
  UserPlus,
  Users,
  Check
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Separator } from '../ui/separator';
import { Badge } from '../ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../ui/dropdown-menu';
import { cn } from '../../lib/utils';
import { toast } from 'sonner';
import { useTheme } from '../../context/ThemeContext';
import axios from 'axios';
import { contactsApi, messengerApi } from '../../api/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function formatFullDate(dateStr) {
  const date = new Date(dateStr);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
}

function getFileIcon(type) {
  switch (type) {
    case 'pdf':
      return <FileText className="w-5 h-5 text-red-400" />;
    case 'xlsx':
    case 'xls':
      return <FileSpreadsheet className="w-5 h-5 text-green-400" />;
    case 'image':
    case 'jpg':
    case 'png':
      return <Image className="w-5 h-5 text-blue-400" />;
    default:
      return <FileText className="w-5 h-5 text-gray-400" />;
  }
}

export default function EmailViewer({ 
  email, 
  onBack, 
  onReply,
  onForward,
  onToggleStar,
  onDelete,
  onArchive,
  labels = [],
  currentUserEmail = ''
}) {
  const { isDarkMode, primaryColor } = useTheme();
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [downloadingIndex, setDownloadingIndex] = useState(null);
  const [isContact, setIsContact] = useState(false);
  const [isFriend, setIsFriend] = useState(false);
  const [friendRequestPending, setFriendRequestPending] = useState(false);
  const [addingContact, setAddingContact] = useState(false);
  const [sendingFriendRequest, setSendingFriendRequest] = useState(false);

  // Check if sender is already a contact or friend
  useEffect(() => {
    if (email?.from_user && email.from_user !== currentUserEmail) {
      checkContactStatus();
      checkFriendStatus();
    }
  }, [email?.from_user, currentUserEmail]);

  const checkContactStatus = async () => {
    try {
      const contacts = await contactsApi.getContacts();
      const found = contacts.some(c => c.email?.toLowerCase() === email.from_user?.toLowerCase());
      setIsContact(found);
    } catch (err) {
      console.error('Failed to check contact status:', err);
    }
  };

  const checkFriendStatus = async () => {
    try {
      const friends = await messengerApi.getFriends();
      const found = friends.some(f => f.email?.toLowerCase() === email.from_user?.toLowerCase());
      setIsFriend(found);
      
      // Also check for pending requests
      const requests = await messengerApi.getFriendRequests();
      const pending = requests.outgoing?.some(r => r.to_email?.toLowerCase() === email.from_user?.toLowerCase());
      setFriendRequestPending(pending || false);
    } catch (err) {
      console.error('Failed to check friend status:', err);
    }
  };

  const handleAddContact = async () => {
    setAddingContact(true);
    try {
      await contactsApi.addContact({
        email: email.from_user,
        name: email.from_name || email.from_user.split('@')[0]
      });
      setIsContact(true);
      toast.success('Contact added!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add contact');
    } finally {
      setAddingContact(false);
    }
  };

  const handleSendFriendRequest = async () => {
    setSendingFriendRequest(true);
    try {
      await messengerApi.sendFriendRequest(email.from_user);
      setFriendRequestPending(true);
      toast.success('Friend request sent!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send friend request');
    } finally {
      setSendingFriendRequest(false);
    }
  };

  // Theme-aware classes
  const bgPrimary = isDarkMode ? 'bg-[#16162a]' : 'bg-[#f8f9fc]';
  const bgSecondary = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const borderColor = isDarkMode ? 'border-[#2d2d44]' : 'border-gray-200';
  const textPrimary = isDarkMode ? 'text-white' : 'text-gray-900';
  const textSecondary = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const textMuted = isDarkMode ? 'text-gray-400' : 'text-gray-500';
  const textDimmed = isDarkMode ? 'text-gray-500' : 'text-gray-400';
  const buttonColor = isDarkMode ? 'text-gray-400' : 'text-gray-600';
  const buttonHover = isDarkMode ? 'hover:text-white hover:bg-[#2d2d44]' : 'hover:text-gray-900 hover:bg-gray-100';
  const dropdownBg = isDarkMode ? 'bg-[#1a1a2e]' : 'bg-white';
  const dropdownText = isDarkMode ? 'text-gray-300' : 'text-gray-700';
  const dropdownHover = isDarkMode ? 'hover:text-white focus:bg-[#2d2d44]' : 'hover:text-gray-900 focus:bg-gray-100';
  const separatorBg = isDarkMode ? 'bg-[#2d2d44]' : 'bg-gray-200';

  const handleDownloadAttachment = async (attachment, index) => {
    setDownloadingIndex(index);
    try {
      let blob;
      let filename = attachment.name;
      
      if (attachment.storage_key) {
        // Download from iDrive via API
        const response = await axios.get(
          `${API}/attachments/download/${email.id}/${attachment.storage_key}`,
          { responseType: 'blob' }
        );
        blob = response.data;
      } else if (attachment.data) {
        // Decode base64 data
        const byteCharacters = atob(attachment.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        blob = new Blob([byteArray], { type: attachment.type });
      } else {
        // Try inline download endpoint
        const response = await axios.get(
          `${API}/attachments/download-inline/${email.id}/${index}`,
          { responseType: 'blob' }
        );
        blob = response.data;
      }
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Downloaded ${filename}`);
    } catch (error) {
      console.error('Download failed:', error);
      toast.error('Failed to download attachment');
    } finally {
      setDownloadingIndex(null);
    }
  };

  if (!email) {
    return (
      <div className={`flex flex-col items-center justify-center h-full ${bgPrimary}`}>
        <Shield className="w-20 h-20 mb-4 text-[#6D4AFF] opacity-30" />
        <p className={`text-lg font-medium ${textMuted}`}>Select a message to read</p>
        <p className={`text-sm ${textDimmed}`}>Your messages are end-to-end encrypted</p>
      </div>
    );
  }

  const emailLabels = (email.labels || []).map(id => labels.find(l => l.id === id)).filter(Boolean);

  return (
    <div className={`flex flex-col h-full ${bgPrimary}`}>
      {/* Toolbar */}
      <div className={`flex items-center justify-between px-4 py-3 border-b ${borderColor}`}>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={onBack}
            className={`${buttonColor} ${buttonHover} md:hidden`}
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onToggleStar(email.id)}
            className={`${buttonColor} ${buttonHover}`}
          >
            <Star className={cn(
              "w-5 h-5",
              email.starred && "fill-yellow-500 text-yellow-500"
            )} />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onArchive(email.id)}
            className={`${buttonColor} ${buttonHover}`}
          >
            <Archive className="w-5 h-5" />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            onClick={() => onDelete(email.id)}
            className={`${buttonColor} hover:text-red-400 ${isDarkMode ? 'hover:bg-[#2d2d44]' : 'hover:bg-gray-100'}`}
          >
            <Trash2 className="w-5 h-5" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className={`${buttonColor} ${buttonHover}`}>
                <Tag className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={`${dropdownBg} ${borderColor} z-50`} align="end">
              {labels.map((label) => (
                <DropdownMenuItem key={label.id} className={`${dropdownText} ${dropdownHover}`}>
                  <span className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: label.color }} />
                  {label.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className={`${buttonColor} ${buttonHover}`}>
                <MoreHorizontal className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className={`${dropdownBg} ${borderColor} z-50`} align="end">
              <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`}>
                <Printer className="w-4 h-4 mr-2" />
                Print
              </DropdownMenuItem>
              <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`}>
                <ExternalLink className="w-4 h-4 mr-2" />
                Open in new window
              </DropdownMenuItem>
              <DropdownMenuSeparator className={separatorBg} />
              <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`}>
                Report spam
              </DropdownMenuItem>
              <DropdownMenuItem className={`${dropdownText} ${dropdownHover}`}>
                Block sender
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReply(email)}
            className={`${textSecondary} ${buttonHover}`}
          >
            <Reply className="w-4 h-4 mr-2" />
            Reply
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onReply(email, true)}
            className={`${textSecondary} ${buttonHover}`}
          >
            <ReplyAll className="w-4 h-4 mr-2" />
            Reply All
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onForward(email)}
            className={`${textSecondary} ${buttonHover}`}
          >
            <Forward className="w-4 h-4 mr-2" />
            Forward
          </Button>
        </div>
      </div>

      {/* Email Content */}
      <ScrollArea className="flex-1">
        <div className="max-w-4xl mx-auto p-6">
          {/* Subject */}
          <div className="mb-6">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
              {/* Date on top for mobile */}
              <div className="flex items-center gap-2 sm:hidden">
                <span className={`text-xs ${textMuted}`}>
                  {new Date(email.created_at).toLocaleDateString('en-US', { 
                    weekday: 'short', 
                    month: 'short', 
                    day: 'numeric',
                    year: 'numeric',
                    hour: 'numeric',
                    minute: '2-digit'
                  })}
                </span>
                {email.encrypted && (
                  <div className="flex items-center gap-1 text-green-500 text-xs bg-green-500/10 px-2 py-0.5 rounded-full">
                    <Lock className="w-3 h-3" />
                    <span>Encrypted</span>
                  </div>
                )}
              </div>
              {/* Subject title */}
              <h1 className={`text-lg sm:text-xl font-semibold ${textPrimary}`}>{email.subject}</h1>
              {/* Labels */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {emailLabels.map((label) => (
                  <Badge
                    key={label.id}
                    className="text-white text-xs"
                    style={{ backgroundColor: label.color }}
                  >
                    {label.name}
                  </Badge>
                ))}
              </div>
            </div>
          </div>

          {/* Sender Info */}
          <div className="flex items-start gap-3 sm:gap-4 mb-6">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-gradient-to-br from-[#6D4AFF] to-[#8B5CF6] flex items-center justify-center flex-shrink-0">
              <span className="text-white font-medium text-sm sm:text-base">{getInitials(email.from_name)}</span>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
                <span className={`${textPrimary} font-medium`}>{email.from_name}</span>
                {/* Encrypted badge - desktop only (shown above on mobile) */}
                {email.encrypted && (
                  <div className="hidden sm:flex items-center gap-1 text-green-500 text-xs">
                    <Lock className="w-3 h-3" />
                    <span>Encrypted</span>
                  </div>
                )}
              </div>
              <div className={`text-sm ${textMuted}`}>
                <span>&lt;{email.from_user}&gt;</span>
              </div>
              
              {/* Add to Contacts / Send Friend Request Buttons */}
              {email.from_user && email.from_user !== currentUserEmail && (
                <div className="flex items-center gap-2 mt-2">
                  {!isContact ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAddContact}
                      disabled={addingContact}
                      className={`h-7 text-xs ${isDarkMode ? 'border-[#2d2d44] text-gray-300 hover:bg-[#2d2d44]' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}
                      data-testid="add-contact-btn"
                    >
                      {addingContact ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <UserPlus className="w-3 h-3 mr-1" />
                      )}
                      Add to Contacts
                    </Button>
                  ) : (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">
                      <Check className="w-3 h-3 mr-1" />
                      Contact
                    </Badge>
                  )}
                  
                  {!isFriend && !friendRequestPending ? (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleSendFriendRequest}
                      disabled={sendingFriendRequest}
                      style={{ borderColor: primaryColor, color: primaryColor }}
                      className={`h-7 text-xs hover:text-white`}
                      onMouseEnter={(e) => e.target.style.backgroundColor = primaryColor}
                      onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                      data-testid="send-friend-request-btn"
                    >
                      {sendingFriendRequest ? (
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                      ) : (
                        <Users className="w-3 h-3 mr-1" />
                      )}
                      Send Friend Request
                    </Button>
                  ) : friendRequestPending ? (
                    <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                      <Loader2 className="w-3 h-3 mr-1" />
                      Request Pending
                    </Badge>
                  ) : (
                    <Badge style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }} className="text-xs">
                      <Users className="w-3 h-3 mr-1" />
                      Friend
                    </Badge>
                  )}}
                </div>
              )}
              
              <button
                onClick={() => setDetailsExpanded(!detailsExpanded)}
                className={`flex items-center gap-1 text-xs ${textDimmed} hover:${textSecondary} mt-2 transition-colors`}
              >
                <span>To: {(email.to || []).join(', ')}</span>
                {detailsExpanded ? (
                  <ChevronUp className="w-3 h-3" />
                ) : (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>

              {detailsExpanded && (
                <div className={`mt-3 p-3 ${bgSecondary} rounded-lg text-sm space-y-1 ${isDarkMode ? '' : 'border ' + borderColor}`}>
                  <p className={textMuted}>
                    <span className={textDimmed}>From:</span> {email.from_name} &lt;{email.from_user}&gt;
                  </p>
                  <p className={textMuted}>
                    <span className={textDimmed}>To:</span> {(email.to || []).join(', ')}
                  </p>
                  {email.cc && email.cc.length > 0 && (
                    <p className={textMuted}>
                      <span className={textDimmed}>Cc:</span> {email.cc.join(', ')}
                    </p>
                  )}
                  <p className={textMuted}>
                    <span className={textDimmed}>Date:</span> {formatFullDate(email.created_at)}
                  </p>
                  <p className={textMuted}>
                    <span className={textDimmed}>Security:</span> {email.encrypted ? 'End-to-end encrypted (AES-256)' : 'Standard TLS'}
                  </p>
                </div>
              )}
            </div>

            <div className={`hidden sm:block text-sm ${textDimmed} flex-shrink-0`}>
              {formatFullDate(email.created_at)}
            </div>
          </div>

          <Separator className={`${separatorBg} my-6`} />

          {/* Email Body */}
          <div className={`prose ${isDarkMode ? 'prose-invert' : ''} max-w-none ${textSecondary} whitespace-pre-wrap`}>
            {email.body}
          </div>

          {/* Attachments */}
          {email.attachments && email.attachments.length > 0 && (
            <div className="mt-8">
              <h3 className={`text-sm font-medium ${textMuted} mb-3`}>
                Attachments ({email.attachments.length})
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {email.attachments.map((attachment, index) => (
                  <div
                    key={index}
                    onClick={() => handleDownloadAttachment(attachment, index)}
                    className={`flex items-center gap-3 p-3 ${bgSecondary} rounded-lg border ${borderColor} hover:border-[#6D4AFF] transition-colors cursor-pointer group`}
                  >
                    {getFileIcon(attachment.type)}
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${textSecondary} truncate`}>{attachment.name}</p>
                      <p className={`text-xs ${textDimmed}`}>{attachment.size}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      disabled={downloadingIndex === index}
                      className={`h-8 w-8 ${buttonColor} ${isDarkMode ? 'hover:text-white' : 'hover:text-gray-900'} opacity-0 group-hover:opacity-100 transition-opacity`}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDownloadAttachment(attachment, index);
                      }}
                    >
                      {downloadingIndex === index ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Quick Reply */}
          <div className={`mt-8 pt-6 border-t ${borderColor}`}>
            <div className="flex gap-3">
              <Button
                onClick={() => onReply(email)}
                className="bg-[#6D4AFF] hover:bg-[#5B3FD9] text-white"
              >
                <Reply className="w-4 h-4 mr-2" />
                Reply
              </Button>
              <Button
                variant="outline"
                onClick={() => onForward(email)}
                className={`${borderColor} ${textSecondary} ${buttonHover}`}
              >
                <Forward className="w-4 h-4 mr-2" />
                Forward
              </Button>
            </div>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
